/**
 * certificateVehicle — does the certificate name the car this contract is about?
 *
 * THE QUESTION THIS ANSWERS IS NOT THE ONE `certificateDates` ANSWERS. That module reads what a
 * statutory certificate SAYS; this one compares two of the things it says — the registration
 * number printed as statutory field 1 and the chassis number printed below field 8 — against the
 * vehicle the contract is written over (`vehicle_registrations.number` and `vehicles.vin`). A
 * certificate that names another car is not this contract's cover, whatever its dates read.
 *
 * NORMALISATION IS UPPERCASE, SPACES, HYPHENS AND DOTS — AND NOTHING ELSE, WHICH IS THE WHOLE
 * RULING. Both halves are mandatory and measured:
 *
 *  - WITHOUT IT, 39 OF 39 PLATES AND 8 OF 39 CHASSIS FALSELY MISMATCH. Every certificate in the
 *    production corpus writes the plate `GE 5312-24` where `vehicle_registrations.number` holds
 *    `GE 5312 24`; and the same insurer's own template prints `NSP130-2117407` on one certificate
 *    and `NSP1302110905` on the next. The separator is not data.
 *  - WITH ANY MORE OF IT, THE RULE STOPS SEEING THE THING IT EXISTS FOR. The three real
 *    disagreements in production are each ONE character, and each is exactly the substitution a
 *    "smart" fold would swallow: `SCP80` for `SCP90` (8/9), `SCO90` for `SCP90` (letter O for
 *    letter P), `NSP230` for `NSP130` (1/2). O↔0 and I↔1 folding is therefore deliberately ABSENT:
 *    it would hide two of the three live defects and buy nothing that has ever been observed.
 *
 * THE SENTENCE IS SYMMETRIC, BECAUSE ON LIVE DATA THE APP IS WRONG TWICE AS OFTEN AS THE PAPER IS.
 * Of the three mismatches in the census, two are typos in `vehicles.vin` (`SCP80`/`SCO90` each
 * appear exactly once in a 289-row table, and the certificate's series is the one the rest of the
 * fleet carries) and one is the insurer's (`NSP230` appears nowhere in the fleet). So the sentence
 * states the disagreement with both values shown and names no corrected one — a sentence asserting
 * that the certificate belongs to another vehicle would be wrong two times out of three.
 *
 * A FIELD COMPARES ONLY WHEN BOTH SIDES ARE PRINTED. A certificate that prints no chassis, a
 * vehicle with no registration row (105 of 289 vehicles have none) and a document the parser
 * refused all produce `absent` rather than `mismatch`: there is nothing to disagree about, and a
 * refusal raised over a field nobody can see is a refusal nobody can clear.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

/** The identifiers a certificate printed, exactly as printed. Absent keys were not on the page. */
export interface CertificateVehicle {
    /** Statutory field 1, verbatim: `GE 5312-24`. */
    registration?: string;
    /** The chassis/frame number, verbatim: `SCP90-5153010` or `NSP1302110905`. */
    chassis?: string;
}

/** The vehicle this contract is written over, as the app holds it. */
export interface ContractVehicle {
    /** `vehicle_registrations.number` — `GE 5312 24`. */
    plate: string | null;
    /** `vehicles.vin`, which is the chassis column despite the name — `SCP90-5153010`. */
    vin: string | null;
}

/** One field's answer. `absent` means one side or the other printed nothing to compare. */
export type IdentifierVerdict = 'match' | 'mismatch' | 'absent';

export interface VehicleMatch {
    /**
     * `mismatch` if ANY compared field differs, `match` if at least one compared and none differ,
     * `unverified` if nothing could be compared at all.
     */
    verdict: 'match' | 'mismatch' | 'unverified';
    registration: IdentifierVerdict;
    chassis: IdentifierVerdict;
    /** What the certificate printed, raw. */
    printed: { registration?: string; chassis?: string };
    /** What the contract's vehicle holds, raw. */
    expected: { plate?: string; vin?: string };
}

/**
 * Uppercase; spaces, hyphens and dots removed.
 *
 * THE DOT IS IN THE LIST FOR THE SAME REASON THE HYPHEN IS — it is a separator an insurer's
 * template may draw and a data-entry clerk may not (`SCP90.5153010`). It is not a character either
 * side's identifiers are made of.
 */
export function normaliseIdentifier(value: string | null | undefined): string {
    return (value ?? '').toUpperCase().replace(/[\s.-]/g, '');
}

/** Both sides present and normalised-equal, both present and unequal, or nothing to compare. */
function compare(printed: string | undefined, held: string | null | undefined): IdentifierVerdict {
    const a = normaliseIdentifier(printed);
    const b = normaliseIdentifier(held);
    if (a === '' || b === '') return 'absent';
    return a === b ? 'match' : 'mismatch';
}

/** What the certificate's identifiers say about the contract's vehicle. */
export function matchCertificateVehicle(
    read: CertificateVehicle,
    vehicle: ContractVehicle,
): VehicleMatch {
    const registration = compare(read.registration, vehicle.plate);
    const chassis = compare(read.chassis, vehicle.vin);

    const fields = [registration, chassis];
    const verdict = fields.includes('mismatch')
        ? 'mismatch'
        : fields.includes('match')
          ? 'match'
          : 'unverified';

    const printed: VehicleMatch['printed'] = {};
    if ((read.registration ?? '').trim() !== '') printed.registration = read.registration;
    if ((read.chassis ?? '').trim() !== '') printed.chassis = read.chassis;

    const expected: VehicleMatch['expected'] = {};
    if ((vehicle.plate ?? '').trim() !== '') expected.plate = vehicle.plate as string;
    if ((vehicle.vin ?? '').trim() !== '') expected.vin = vehicle.vin as string;

    return { verdict, registration, chassis, printed, expected };
}

/**
 * THE WHOLE CLOSING IMPERATIVE TURNS ON THE DOOR, NOT ONE CLAUSE OF IT.
 *
 * A document being CHOSEN can still be refused, and saying so is the point of the staged tail. One
 * already in storage cannot be un-filed by a sentence — telling an admin that a certificate they
 * are looking at "cannot be filed" when it demonstrably was is the kind of line that teaches people
 * to ignore the rest of it. `Check it BEFORE FILING IT` is the same lie one clause earlier, about
 * the same already-filed document, so it goes the same way: the on-file half names the only thing
 * there is left to do, which is read the paper that is already on file against the record.
 *
 * `both` FOLLOWS THE SENTENCE ON BOTH HALVES, because it is counting the fields that disagree and
 * that count does not depend on which door asked.
 */
const tailFor = (cannotFile: boolean, both: boolean): string =>
    cannotFile
        ? `Check ${both ? 'both' : 'it'} before filing it.` +
          ' It cannot be filed against this contract.'
        : `Check ${both ? 'both' : 'it'} against the filed document.`;

/** The typographic apostrophe, which is this canvas's convention for prose the admin reads. */
const APOSTROPHE = '’';

/**
 * The one sentence this rule ever says, or '' when there is nothing to refuse.
 *
 * ONLY THE DIFFERING FIELDS ARE PRINTED, PLATE FIRST. A chassis that agrees is not evidence the
 * admin needs while a plate does not, and reprinting it would bury the one character that moved.
 * The plate leads because it is the stronger signal: 39 of 39 plates match in production, so a
 * plate disagreement is almost certainly the wrong document, while the chassis is a hand-typed
 * 13-to-14-character string on both sides and disagrees on 3 of 39.
 *
 * `cannotFile` IS THE SECOND ARGUMENT RATHER THAN A SECOND SENTENCE, so the copy stays one family
 * with one author: the staged doors pass `true`, the on-file advisory passes nothing. It governs
 * the closing imperative WHOLE — see `tailFor`.
 */
export function vehicleRefusal(match: VehicleMatch, cannotFile = false): string {
    if (match.verdict !== 'mismatch') return '';

    const names: string[] = [];
    const held: string[] = [];
    if (match.registration === 'mismatch') {
        names.push(match.printed.registration as string);
        held.push(match.expected.plate as string);
    }
    if (match.chassis === 'mismatch') {
        names.push(`chassis ${match.printed.chassis}`);
        held.push(`chassis ${match.expected.vin}`);
    }

    // THE SAME CONNECTOR ON BOTH HALVES. The two lists are parallel — the same fields, in the same
    // order, from the two sides — and a sentence that reads them differently invites the reader to
    // look for a difference that is not there.
    const both = names.length > 1;
    return (
        `This certificate names ${names.join(' and ')}; ` +
        `this contract${APOSTROPHE}s vehicle is ${held.join(' and ')}. ` +
        tailFor(cannotFile, both)
    );
}

/**
 * THE PREFIX THE REFUSAL MAP MATCHES ON, exported here so the sentence and the thing that
 * recognises it cannot drift apart. Every sentence above opens with it, and nothing else on this
 * canvas does.
 */
export const VEHICLE_REFUSAL_PREFIX = 'This certificate names ';
